import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { Element } from 'react-scroll';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import cx from 'classnames';
// Component
import AboutCompany from './AboutCompany/AboutCompany';
import CompanyContactUs from './CompanyContactUs/CompanyContactUs';
import ShareButtonsRow from '../ShareButtonsRow/ShareButtonsRow';
import DescriptionTextBlock from '../DescriptionTextBlock/DescriptionTextBlock';
import Portfolio from '../Portfolio/Potrfolio';
import PromoBlock from '../PromoBlock/PromoBlock';
import CompanyInfoBlock from './CompanyInfoBlock/CompanyInfoBlock';
import CompanyProperties from './CompanyProperties/CompanyProperties';
import CompanyMap from './CompanyMap/CompanyMap';
import CompanyRealtors from '../CompanyRealtors/CompanyRealtors';
import getLocalizedData from '../../utils/getLocalizedData';
// Constants
import {
  DEFAULT_MAIN_IMAGE, imageModifierTypes,
  language,
  MAIN_IMAGES, MAIN_IMAGES_MOBILE, PROFESSIONAL_TYPE_BROKER,
  PROFESSIONALS_TYPES,
  realtorLandingBottomBackground,
  shareUrl,
  ENTITY_TYPES,
  PRODUCTION_IMAGE_DOMAIN_BASE_URL
} from '../../constants';
import multiLanguageTags from '../../helpers/multiLanguageHeadTags';
// Helpers
import { AnalyticsEventService } from '../../services';
import formatAddress from '../../utils/formatAddress';
import makeSharingUrl from '../../utils/makeSharingUrl';
import checkIsFutureDate from '../../utils/checkIsFutureDate';
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';
// styles
import * as styles from './Company.scss';
import modifyImageLink from '../../utils/modifyImageLink';
import HeaderPhones from './HeaderPhones/HeaderPhones';
import ConsultationBlock from './ConsultationBlock/ConsultationBlock';

const getLocalizedField = (data, field, locale) => {
  if (!data || !data[field]) {
    return '';
  }

  if (!data[field][locale] && !data[field][language.default]) {
    return '';
  }

  return data[field][locale] || data[field][language.default];
};

const getLocalizeCompanyData = (data, locale) => ({
  ...data,
  name: getLocalizedField(data, 'name', locale),
  title: getLocalizedField(data, 'title', locale),
  description: getLocalizedField(data, 'description', locale),
  additional_info: getLocalizedField(data, 'additional_info', locale),
  isNameAtDefaultLang: !get(data, ['name', locale]),
  isTitleAtDefaultLang: !get(data, ['title', locale]),
  isDescriptionAtDefaultLang: !get(data, 'description', locale)
});

export const OPTION_TYPES_COMPANY = {
  SALE: 'forSale',
  RENT: 'forRent'
};
export const DEFAULT_DISPLAYED_PINS = OPTION_TYPES_COMPANY.SALE;
const consultationImage = `${PRODUCTION_IMAGE_DOMAIN_BASE_URL}/images/professionals/partnership.webp`;

class Company extends Component {
  constructor(props) {
    super(props);
    const propertiesForSell = this.getPropertiesFor('buy', props);
    const propertiesForRent = this.getPropertiesFor('rent', props);

    this.state = {
      propertiesForSell,
      propertiesForRent,
      captcha: null,
      showCaptcha: false
    };
  }

  componentWillMount() {
    this.detectLanguage(this.props);
  }

  componentWillReceiveProps(nextProps) {
    const {
      properties,
      formSubmitting,
      formSubmitted,
      formError,
      formConsultationError,
      formConsultationSubmitted,
      formConsultationSubmitting,
      company,
      descriptionData,
      locale,
      additionalInfoData
    } = nextProps;
    if (locale !== this.props.locale) {
      this.props.resetTranslate();

      this.detectLanguage(nextProps);
    }
    if (
      descriptionData.showOriginal &&
      !descriptionData.loading &&
      !descriptionData.loaded
    ) {
      this.props.translateDescription({ q: company.description.he });
    }

    if (
      additionalInfoData.showOriginal &&
      !additionalInfoData.loading &&
      !additionalInfoData.loaded
    ) {
      this.props.translateAdditionalInfo({ q: company.additional_info.he });
    }

    if ((properties && properties.length && (!this.props.properties || !this.props.properties.length))
      || !isEqual(properties, this.props.properties)) {
      const propertiesForSell = this.getPropertiesFor('buy', nextProps);
      const propertiesForRent = this.getPropertiesFor('rent', nextProps);

      this.setState({
        propertiesForSell,
        propertiesForRent
      });
    }

    if (this.props.formSubmitting && !formSubmitting && formSubmitted && !formError) {
      this.props.reset();
      this.props.resetReduxForm('company-contact-us');

      if (!formError) {
        this.openSuccessMessage();
      }
    }

    if (this.props.formConsultationSubmitting
      && !formConsultationSubmitting && formConsultationSubmitted && !formConsultationError) {
      this.props.consultationReset();
      this.props.resetReduxForm('Consultation-Form');

      if (!formConsultationError) {
        this.openSuccessMessage();
      }
    }
  }

  componentWillUnmount = () => {
    this.props.resetTranslate();
  };

  getPropertiesFor = (optionType, props = this.props) => {
    if (!props.properties) {
      return null;
    }

    const additionalTypes = (optionType === 'rent') ? ['rent-short'] : [];

    return props.properties && props.properties
      .filter(prop => [optionType, ...additionalTypes]
        .includes(prop.option_type)
      );
  };

  getMainImage = () => {
    const { company: { type, main_image, is_community_member }, isMobile, isRtl } = this.props;
    if (main_image) { return main_image; }

    const IMAGES = isMobile ? MAIN_IMAGES_MOBILE : MAIN_IMAGES;
    const brokerType = !isMobile && !isRtl ? `${PROFESSIONAL_TYPE_BROKER}_ltr` : PROFESSIONAL_TYPE_BROKER;

    const key = (is_community_member && type === PROFESSIONALS_TYPES.AGENCY) ? brokerType : type;
    return IMAGES[key] || DEFAULT_MAIN_IMAGE;
  };

  detectLanguage = (props) => {
    const { locale, detectDescriptionLanguage, detectAdditionalInfoLanguage } = props;
    const description = get(props, 'company.description', null);
    const additionalInfo = get(props, 'company.additional_info', null);

    if (description) {
      const { result } = getLocalizedData(description, locale);

      detectDescriptionLanguage({
        q: result,
        locale
      });
    }

    if (additionalInfo) {
      const { result } = getLocalizedData(additionalInfo, locale);

      detectAdditionalInfoLanguage({
        q: result,
        locale
      });
    }
  }

  openSuccessMessage = () => this.props.openMessageModal({
    message: (
      <div className="successMessage">
        <span>{this.props.translate('companyPage', 'contactsForm', 'submitting_success')}</span>
      </div>
    ),
    messageModalClassName: 'company-message-modal'
  });

  fetchReCaptchaToken = (token) => {
    this.setState({ captcha: token });
  }

  formFeedBackSubmitHandler = formValues => {
    const { email, is_community_member, ...company } = this.props.company;
    const { captcha } = this.state;
    const receiver_name = getLocalizedField(company, 'title', this.props.locale);

    if (captcha) {
      this.props.sendCompanyContactUsForm({
        captcha,
        ...formValues,
        receiver_name,
        to: email,
        is_community_member
      });
    }

    // hermione event
    AnalyticsEventService.createCompanyEvent(this.props.companyId);
  };

  formConsultationSubmitHandler = formValues => {
    const { email, is_community_member, ...company } = this.props.company;
    const receiver_name = getLocalizedField(company, 'name', this.props.locale);

    this.props.sendConsultationForm({
      ...formValues,
      receiver_name,
      to: email,
      message: formValues.message || ' - ',
      is_community_member
    });

    // hermione event
    AnalyticsEventService.createCompanyEvent(this.props.companyId);
  };

  openDetails = (evt, propertySlug) => {
    const entityType = ENTITY_TYPES.PROPERTY;

    this.props.openPopupWithDetails(evt, propertySlug, entityType);
  };

  toggleFavoriteProperty = (property) => {
    this.props.toggleFavorite({ property, userToken: this.props.userToken });
  }

  handleShowCaptcha = () => {
    this.setState({ showCaptcha: true });
  }

  renderCallUsMessage = () => (
    <div>
      <div>{this.props.translate('callOrLeaveMessage')}:&nbsp;&nbsp;</div>
      <a href={`tel:${this.props.company.phone_number}`}>{formatPhoneNumber(this.props.company.phone_number)}</a>
    </div>
  );

  renderSubtitleMessage = () => this.props.company.type === PROFESSIONALS_TYPES.MORTGAGE
    ? this.props.translate('companyPage', 'mortgageSubtitle').toString()
    : '';

  renderConsultationForm = (companyName, className, field_id) => (
    <ConsultationBlock
      locale={this.props.locale}
      field_id={field_id}
      companyName={companyName}
      className={className}
      onSubmit={this.formConsultationSubmitHandler}
      formSubmitted={this.props.formConsultationSubmitted}
      formSubmitting={this.props.formConsultationSubmitting}
      formConsultationError={this.props.formConsultationError}
      isCommunityMember={this.props.company.is_community_member}
    />
  );

  renderContactUsForm = companyName => (
    <CompanyContactUs
      fetchReCaptchaToken={this.fetchReCaptchaToken}
      captcha={this.state.captcha}
      locale={this.props.locale}
      companyName={companyName}
      translate={this.props.translate}
      onSubmit={this.formFeedBackSubmitHandler}
      isRtl={this.props.isRtl}
      formSubmitting={this.props.formSubmitting}
      formSubmitted={this.props.formSubmitted}
      formError={this.props.formError}
      isCommunityMember={this.props.company.is_community_member}
      onFocus={this.handleShowCaptcha}
      showCaptcha={this.state.showCaptcha}
    />
  );

  renderShareButton = sharingData => (
    <ShareButtonsRow
      isNative={this.props.isNative}
      sharingData={sharingData}
      isMobile={this.props.isMobile}
      locale={this.props.locale}
      copyLinkTitle={`${this.props.translate('copyLink')}`}
      shareTitle={`${this.props.translate('companyPage', 'shareTitle')}`}
      className="companySocialIcons"
      isHideShareTitle
    />
  );

  renderPhones = phone => (
    <HeaderPhones
      translate={this.props.translate('companyPage')}
      phoneNumber={phone}
      locale={this.props.locale}
      isMobile={this.props.isMobile}
      className={styles.phoneBlock}
      callToActionText={this.props.translate('companyPage', 'callNowText').toString()}
      isIconFill
    />
  );

  renderPortfolio = () => {
    if (!get(this.props, 'company.images.length') && !get(this.props, 'company.videos.length')) { return null; }

    return (
      <div className={styles.portfolioWrapper}>
        <div className={styles.container}>
          <Portfolio
            isDetailsModalOpen={this.props.isDetailsModalOpen}
            images={this.props.company.images || []}
            videos={this.props.company.videos || []}
            portfolioTitle={this.props.translate('companyPage',
              (this.props.company.type === PROFESSIONALS_TYPES.DESIGNER)
                ? 'PortfolioTitle'
                : 'galleryTitle'
            )}
            translate={this.props.translate('propertyDetails')}
            changeLightboxActiveSlide={this.props.changeLightboxActiveSlide}
            isOpen={this.props.isLightboxOpen}
            hideLightbox={this.props.hideLightbox}
            locale={this.props.locale}
            isRtl={this.props.isRtl}
            isMobile={this.props.isMobile}
            openLightbox={this.props.openLightbox}
            currentImage={this.props.currentImage}
          />
        </div>
      </div>
    );
  };

  renderProperties = (localizedCompanyName) => {
    if (this.props.company.type !== PROFESSIONALS_TYPES.AGENCY || !get(this.props, 'properties.length')) {
      return null;
    }

    return (
      <div className={styles.container}>
        <CompanyProperties
          translate={this.props.translate}
          locale={this.props.locale}
          isRtl={this.props.isRtl}
          isMobile={this.props.isMobile}
          forSale={this.state.propertiesForSell}
          forRent={this.state.propertiesForRent}
          openDetails={this.openDetails}
          companyName={localizedCompanyName}
          toggleFavorite={this.toggleFavoriteProperty}
          favouritePropertiesIDs={this.props.favouritePropertiesIDs}
        />
      </div>
    );
  };

  renderPromoBlock = () => {
    const advantages = get(this.props, 'company.advantages', []);
    if (!advantages.length) { return null; }

    return (
      <div>
        <div className={styles.container}>
          <PromoBlock
            title={this.props.translate('companyPage', 'promoTitle')}
            locale={this.props.locale}
            advantages={advantages}
          />
        </div>
      </div>
    );
  };

  renderAdditionalInfo = (contentText, addClass) => {
    if (!contentText) { return null; }

    const descriptionText = !this.props.additionalInfoData.showOriginal
    ? contentText
    : this.props.additionalInfoData.translatedText;

    return (
      <div className={styles.descriptionTextBlockWrapper}>
        <div className={cx(styles.container, styles.flexContainer, addClass)}>
          <DescriptionTextBlock
            className="companyDescriptions"
            contentText={descriptionText}
            locale={this.props.locale}
            translate={this.props.translate}
            title="additionTitle"
            description={this.props.company.additional_info}
            descriptionData={this.props.additionalInfoData}
            toggleDescriptionTranslation={this.props.toggleAdditionalInfoTranslation}
            isTranslate
          />
        </div>
      </div>
    );
  };

  renderMap = () => {
    if (this.props.company.type !== PROFESSIONALS_TYPES.AGENCY) {
      return null;
    }

    return (
      <div className={styles.mapWrapper}>
        <div className={styles.container}>
          <CompanyMap
            translate={this.props.translate}
            locale={this.props.locale}
            isMobile={this.props.isMobile}
            isRtl={this.props.isRtl}
            forSale={this.state.propertiesForSell}
            forRent={this.state.propertiesForRent}
            reviewedPins={this.props.reviewedPins}
            setPinAsReviewed={this.props.setPinAsReviewed}
            openDetails={this.openDetails}
            overrideCurrency={this.props.overrideCurrency}
          />
        </div>
      </div>
    );
  };

  renderCompanyRealtors = () => {
    const { company: { is_advanced }, locale } = this.props;

    const realtors = get(this.props, 'company.realtors', []);

    if (!realtors.length) { return null; }

    return (
      <div className={styles.realtorsBlock}>
        <div className={styles.title}>{this.props.translate('companyPage', 'officeStaff')}</div>
        <CompanyRealtors
          locale={locale}
          isAdvanced={is_advanced}
          realtors={realtors}
        />
      </div>
    );
  };

  render() {
    const bgPhotos = {
      imgCompany: { backgroundImage: `url('${this.getMainImage()}')` },
      imgConsultation: { backgroundImage: `url('${consultationImage}')` },
      bgContactsForm: { backgroundImage: `url('${realtorLandingBottomBackground}')` }
    };
    const { company, locale, route, companyId, descriptionData } = this.props;

    const localizeCompanyData = getLocalizeCompanyData(company, locale);
    const phoneNumber = (localizeCompanyData.phone_number);
    const { image, is_advanced } = company;
    const SEOTitle = localizeCompanyData.title || localizeCompanyData.name;
    const branches = (localizeCompanyData && localizeCompanyData.branches) || [];
    const address = (localizeCompanyData && localizeCompanyData.address)
      ? formatAddress(localizeCompanyData.address[locale] || localizeCompanyData.address.en)
      : '';
    const sharingData = {
      sharingUrl: makeSharingUrl(`/company/${companyId}`, locale),
      sharingTitle: SEOTitle
    };
    const metaTitle = `${SEOTitle} | \u202BOnMap`;
    const formattedImage = modifyImageLink(image, imageModifierTypes.LOGO);
    const multiLanguage = multiLanguageTags(route, locale);

    const descriptionText = !descriptionData.showOriginal
      ? localizeCompanyData.description
      : descriptionData.translatedText;

    const { page = {} } = company || {};
    const isSharingAvailable = !!page.enabled && checkIsFutureDate(page.until);

    return (
      <div className={styles.pageWrapper}>
        <Helmet titleTemplate={this.props.translate('title', 'titleTemplate').toString()}>
          <title>{SEOTitle}</title>
          <meta property="title" content={metaTitle} />
          <meta property="og:title" content={metaTitle} />
          <meta property="og:image" content={formattedImage} />
          <meta property="og:image:width" content={250} />
          <meta property="og:image:height" content={250} />
          <meta property="og:url" content={`${shareUrl}${route}`} />
          <meta name="description" content={localizeCompanyData.description} />
          <meta property="og:description" content={localizeCompanyData.description} />
          <meta property="x:title" content={metaTitle} />
          <meta property="x:description" content={localizeCompanyData.description} />
          <meta property="x:image" content={formattedImage} />
          <meta property="x:image:alt" content={localizeCompanyData.name} />
          <link rel="canonical" href={`${shareUrl}${route}`} />
          {multiLanguage && multiLanguage.map(item =>
            (<link key={`${item.hrefLang}-key`} rel={item.rel} hrefLang={item.hrefLang} href={item.href} />))}
          {(this.props.locale !== language.default) && (<meta name="robots" content="noindex,nofollow" />)}
        </Helmet>
        <CompanyInfoBlock
          translate={this.props.translate('companyPage')}
          title={localizeCompanyData.name}
          phoneNumber={phoneNumber}
          isMobile={this.props.isMobile}
          locale={this.props.locale}
          scrollToBlock="company-page-content"
        />

        <div className={cx(styles.headerTop, { [styles.brokerCommunity]: company.is_community_member })}>
          <span className={styles.bgCover} style={bgPhotos.imgCompany} />
          <div className={styles.container}>
            <AboutCompany
              translate={this.props.translate('companyPage')}
              companyName={localizeCompanyData.name}
              companyLogo={formattedImage}
              phoneNumber={phoneNumber}
              branches={branches}
              address={address}
              company={company}
              locale={locale}
              isMobile={this.props.isMobile}
            />
          </div>
          <div className={styles.container}>
            {this.renderConsultationForm(
              localizeCompanyData.name,
              'headerTopForm',
              'header_top_form_phone'
            )}
          </div>
          {isSharingAvailable && (
            <div className={styles.container}>
              {this.renderShareButton(sharingData)}
            </div>
          )}
        </div>
        {company.is_community_member && <div className={styles.infoMessage}>משווקים נדל״ן ב-4 שפות</div>}
        <Element name="company-page-content">
          {is_advanced && this.renderPromoBlock()}
          <div className={styles.descriptionTextBlockWrapper}>
            <div className={cx(styles.container, styles.flexContainer)}>
              <DescriptionTextBlock
                className="companyDescriptions"
                contentText={descriptionText}
                locale={locale}
                translate={this.props.translate}
                title="descriptionTitle"
                description={this.props.company.description}
                descriptionData={this.props.descriptionData}
                toggleDescriptionTranslation={this.props.toggleDescriptionTranslation}
                isTranslate
              />
            </div>
          </div>
          {is_advanced && this.renderPortfolio()}
          {this.renderProperties(localizeCompanyData.name)}
          <div className={styles.consultationWrapper} style={bgPhotos.imgConsultation}>
            <div className={styles.container}>
              {this.renderConsultationForm(
                localizeCompanyData.name,
                'consultationForm',
                'consultation_form_phone'
              )}
              {this.renderPhones(phoneNumber)}
            </div>
          </div>
          <div className={styles.container}>
            {this.renderCompanyRealtors()}
          </div>
          {this.renderMap()}
          {this.renderAdditionalInfo(localizeCompanyData.additional_info, 'additionalTitle')}
          <Element name="contacts-form-block" className={styles.contactFormWrapper} style={bgPhotos.bgContactsForm}>
            <div className={styles.container}>
              {this.renderContactUsForm(localizeCompanyData.name)}
            </div>
          </Element>
        </Element>
      </div>
    );
  }
}

Company.propTypes = {
  company: PropTypes.object,
  properties: PropTypes.array,
  routeData: PropTypes.object,
  consultation: PropTypes.object,
  formError: PropTypes.object,
  formConsultationError: PropTypes.object,
  formConsultationSubmitted: PropTypes.bool,
  formConsultationSubmitting: PropTypes.bool,
  translate: PropTypes.func,
  openMessageModal: PropTypes.func,
  sendConsultationForm: PropTypes.func,
  reset: PropTypes.func,
  resetReduxForm: PropTypes.func,
  consultationReset: PropTypes.func,
  sendCompanyContactUsForm: PropTypes.func,
  showMessage: PropTypes.func,
  locale: PropTypes.string,
  route: PropTypes.string,
  companyId: PropTypes.string,
  isMobile: PropTypes.bool,
  reviewedPins: PropTypes.array,
  setPinAsReviewed: PropTypes.func,
  formSubmitting: PropTypes.bool,
  formSubmitted: PropTypes.bool,
  isLightboxOpen: PropTypes.bool,
  isDetailsModalOpen: PropTypes.bool,
  openLightbox: PropTypes.func,
  changeLightboxActiveSlide: PropTypes.func,
  hideLightbox: PropTypes.func,
  currentImage: PropTypes.number,
  isRtl: PropTypes.bool,
  descriptionData: PropTypes.object,
  additionalInfoData: PropTypes.object,
  translateDescription: PropTypes.func,
  translateAdditionalInfo: PropTypes.func,
  detectAdditionalInfoLanguage: PropTypes.func,
  detectDescriptionLanguage: PropTypes.func,
  isNative: PropTypes.bool,
  toggleDescriptionTranslation: PropTypes.func,
  toggleAdditionalInfoTranslation: PropTypes.func,
  resetTranslate: PropTypes.func,
  openPopupWithDetails: PropTypes.func,
  toggleFavorite: PropTypes.func,
  userToken: PropTypes.string,
  favouritePropertiesIDs: PropTypes.array,
  overrideCurrency: PropTypes.string
};

export default Company;
